import assetUtils, { Asset } from 'app-lib/assetUtils'
import { autoFormat } from 'app-lib/numberUtils'
import { chainConfigs } from 'bzx-common/src/config/appConfig'
import { airdrops } from 'bzx-common/src/services/AirdropService'
import { observer } from 'mobx-react'
import React, { CSSProperties } from 'react'
import ButtonChainSwitch from 'shared-components/ButtonChainSwitch'
import ExternalLink from 'shared-components/ExternalLink'
import { Button, Icon, Input } from 'ui-framework'
import CompensationVM from './CompensationVM'

const styles: Record<string, CSSProperties> = {
  inputWrapper: { maxWidth: '500px' },
  assetName: { width: '56px' },
}

const airdropContract = airdrops.P125_COMPENSATION1.contract
const airdropContractLink = `https://polygonscan.com/address/${airdropContract}`
const p125Info = assetUtils.getInfo(137, Asset.P125)
const p125Link = `https://polygonscan.com/address/${p125Info.address}`

const priceReference = [
  { asset: Asset.AAVE, price: '339.4' },
  { asset: Asset.AUTO, price: '1088.6' },
  { asset: Asset.BNB, price: '601' },
  { asset: Asset.BTCB, price: '61378' },
  { asset: Asset.BUSD, price: '1' },
  { asset: Asset.BZRX, price: '0.442' },
  { asset: Asset.CAKE, price: '19.85' },
  { asset: Asset.DOGE, price: '0.26' },
  { asset: Asset.ETH, price: '4476' },
  { asset: Asset.IAAVE, price: '1.034' },
  { asset: Asset.IAUTO, price: '1.0355' },
  { asset: Asset.IBNB, price: '1.0373' },
  { asset: Asset.IBUSD, price: '1.0258' },
  { asset: Asset.ICAKE, price: '1.0926' },
  { asset: Asset.IDOGE, price: '1.00659' },
  { asset: Asset.IETH, price: '1.0047 / 1.0026' },
  { asset: Asset.ILINK, price: '1.0033' },
  { asset: Asset.IMATIC, price: '1.004' },
  { asset: Asset.IUSDC, price: '1.03' },
  { asset: Asset.IUSDT, price: '1.00549 / 1.048' },
  { asset: Asset.IWBTC, price: '1.0029 / 1.0052' },
  { asset: Asset.LINK, price: '31.6' },
  { asset: Asset.MATIC, price: '1.9' },
  { asset: Asset.USDC, price: '1' },
  { asset: Asset.USDT, price: '1' },
]

export function P125Compensation({ vm }: { vm: CompensationVM }) {
  const { P125_COMPENSATION1 } = vm.airdropStore.airdropsInfo
  const { chainId, isConnected } = vm.rootStore.web3Connection

  return (
    <div className="grid-fluid-colmin-22em">
      <div className="panel--simple md-padded-2">
        <h3 className="panel__header title-section">How does it work?</h3>
        <div className="txt-explanation txt-read-optimized">
          <p className="txt-warning">
            <b>You will receive your P125 compensation on Polygon</b>, regardless of whether your
            tokens were lost on BSC or Polygon.
          </p>

          <ol className="list-spaced-1 txt-explanation">
            <li>Insert your address to know your compensation.</li>
            <li>Check if your compensation is what you expect.</li>
            <li>Report any issue in the compensation channel.</li>
            <li>Wait. You will be able to claim on Polygon soon.</li>
          </ol>

          <h4 className="h4--muted">How many P125 tokens should I get?</h4>

          <p>
            The amount of P125 tokens you will receive will be the equivalent in USD of your total
            loss with an additional 25%.
          </p>
          <p>For example, if your loss was worth 1000$, you will receive 1250 P125 Tokens.</p>

          <Button variant="link" onClick={vm.priceRefDialog.toggle} className="margin-bottom-1">
            {vm.priceRefDialog.visible ? 'Hide' : 'View'} price reference
          </Button>

          {vm.priceRefDialog.visible && (
            <div>
              <p>
                The prices of the tokens are taken from the moment of the loss. There may be a
                slight difference with the final calculation.
              </p>

              <ul className="list-spaced-1">
                <li>BSC: Nov-05-2021 11:07:19 AM +UTC (Block 12386616)</li>
                <li>Polygon: Nov-05-2021 11:06:58 AM +UTC (Block 21009327)</li>
              </ul>
              <div className="compensation__prices margin-bottom-1">
                {priceReference.map((ref) => {
                  return (
                    <div key={ref.asset}>
                      <span className="inline-block" style={styles.assetName}>
                        {ref.asset}
                      </span>
                      ${ref.price}
                    </div>
                  )
                })}
              </div>
              <p>
                Note: Where there are 2 prices, the first one is the price registered on BSC and the
                second one is on Polygon.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="panel--simple md-padded-2">
        <h3 className="panel__header title-section flex-row-center">
          <Icon name="token-p125" size={24} />
          <span className="margin-left-1ch">P125 Compensation</span>
          {chainId === 137 && isConnected && (
            <Button
              className="flex-row-end"
              variant="blue-inverse"
              size="narrow"
              onClick={vm.addP125Token}>
              Add P125 to Wallet
            </Button>
          )}
        </h3>
        {!vm.rootStore.web3Connection.walletAddress && (
          <p className="txt-warning">You need to connect your wallet to claim.</p>
        )}
        {chainId !== 137 && (
          <div className="flex-center">
            <ButtonChainSwitch chainId={137}>
              Switch to {chainConfigs[137].chainDetails.shortName}
            </ButtonChainSwitch>
          </div>
        )}

        {chainId === 137 && (
          <>
            <form onSubmit={vm.checkAirdrop} className="margin-bottom-1">
              <label className="txt-subtitle margin-bottom-05" htmlFor="input-wallet-address">
                Wallet Address
              </label>
              <div className="input-with-sticked-btn" style={styles.inputWrapper}>
                <Input
                  id="input-wallet-address"
                  onChange={vm.set}
                  onChangeEmit="name-value"
                  name="inputAddress"
                  value={vm.inputAddress}
                />
                <Button
                  variant="green"
                  disabled={!vm.canCheckP125}
                  type="submit"
                  isLoading={vm.airdropStore.airdropInfoPending}>
                  Check
                </Button>
              </div>
            </form>

            {P125_COMPENSATION1.token.address && (
              <>
                <p className="txt-size-12 panel--alternative padded-1">
                  You are entitled to{' '}
                  <b className="txt-positive" title={P125_COMPENSATION1.amount.toFixed()}>
                    {autoFormat(P125_COMPENSATION1.amount)}
                  </b>{' '}
                  P125 debt tokens.
                </p>
                {vm.airdropStore.airdropsInfo.P125_COMPENSATION1.userClaimed ? (
                  <p className="txt-warning margin-bottom-2">You have already claimed.</p>
                ) : (
                  <p className="txt-warning margin-bottom-2 txt-read-optimized">
                    Verify that your compensation is what you are expecting. <br />
                    You will receive P125 tokens on Polygon.
                  </p>
                )}

                {P125_COMPENSATION1.amount.gt(0) && vm.canCheck && (
                  <div>
                    {isConnected && !vm.inputAddressEqualConnected && (
                      <p className="txt-warning">
                        The wallet address you are checking is different than the one you are
                        connected to.
                      </p>
                    )}
                    <Button
                      variant="green"
                      className="btn--responsive"
                      disabled={!vm.canClaimP125}
                      isLoading={vm.airdropStore.airdropPending}
                      onClick={vm.airdropStore.claimP125Compensation1}>
                      Claim
                    </Button>
                  </div>
                )}
              </>
            )}

            <div className="panel--alternative padded-1 margin-top-2">
              <div className="margin-bottom-1">
                <h4 className="h4--muted margin-bottom-05">Airdrop contract</h4>
                <ExternalLink
                  className="link--discreet truncate--inline max-width-100p"
                  href={airdropContractLink}>
                  {airdropContract}
                </ExternalLink>
              </div>
              <div>
                <h4 className="h4--muted margin-bottom-05">P125 contract</h4>
                <ExternalLink
                  className="link--discreet truncate--inline max-width-100p"
                  href={p125Link}>
                  {p125Info.address}
                </ExternalLink>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default observer(P125Compensation)
